import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X, Inbox } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const typeIcon = {
  price_approved: '✓',
  price_rejected: '×',
  price_flagged: '!',
  price_pending: '…',
  price_alert: '▲',
  weekly_summary: '▤',
  user_registered: '+',
  welcome: '★',
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const NotificationBell = ({ variant = 'light' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const prevCountRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const [list, c] = await Promise.all([
        api.get('/notifications', { params: { limit: 20 } }),
        api.get('/notifications/unread-count'),
      ]);
      setItems(list.data);
      const newCount = c.data.count || 0;
      // Toast on new arrivals (only if dropdown closed)
      if (newCount > prevCountRef.current && prevCountRef.current !== 0 && !open) {
        const newest = list.data.find(n => !n.read);
        if (newest) toast(newest.title, { description: newest.body });
      }
      prevCountRef.current = newCount;
      setCount(newCount);
    } catch (e) { /* noop */ }
  }, [open]);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [user, load]);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Listen for SW notification clicks
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') { setOpen(true); load(); }
    };
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', handler);
    return () => { if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', handler); };
  }, [load]);

  const markRead = async (id) => {
    setItems(items.map(n => n.id === id ? { ...n, read: true } : n));
    setCount(c => Math.max(0, c - 1));
    try { await api.post(`/notifications/${id}/read`); } catch (e) { /* noop */ }
  };

  const markAllRead = async () => {
    setItems(items.map(n => ({ ...n, read: true })));
    setCount(0);
    try { await api.post('/notifications/read-all'); } catch (e) { /* noop */ }
  };

  const triggerOpen = () => {
    setOpen(o => !o);
    if (!open) load();
  };

  // Destination when a notification is clicked (role-aware)
  const targetFor = (n) => {
    const isAdmin = user?.role === 'admin';
    const eid = n.data?.price_id;
    switch (n.type) {
      case 'price_flagged':
        return isAdmin ? `/admin/prices?tab=anomaly${eid ? `&entry=${eid}` : ''}` : '/app/history';
      case 'price_pending':
        return isAdmin ? `/admin/prices?tab=all${eid ? `&entry=${eid}` : ''}` : null;
      case 'price_change_request':
        return isAdmin ? '/admin/tracking-requests' : null;
      case 'price_approved':
      case 'price_rejected':
        return isAdmin ? `/admin/prices?tab=all${eid ? `&entry=${eid}` : ''}` : '/app/history';
      case 'price_alert':
        return isAdmin ? `/admin/prices?tab=all${eid ? `&entry=${eid}` : ''}` : null;
      case 'weekly_summary':
        return isAdmin ? '/admin/prices?tab=anomaly' : null;
      case 'user_registered':
        return isAdmin ? '/admin/users' : null;
      default:
        return null;
    }
  };

  const onItemClick = (n) => {
    if (!n.read) markRead(n.id);
    const t = targetFor(n);
    if (t) { setOpen(false); navigate(t); }
  };

  if (!user) return null;

  const dark = variant === 'dark';

  return (
    <div className="relative" ref={ref}>
      <button onClick={triggerOpen} className={`w-9 h-9 rounded-full flex items-center justify-center relative transition ${dark ? 'bg-[#f3f0e9] hover:bg-[#e8e5de]' : 'bg-white border border-[#e8e5de] hover:bg-[#f3f0e9]'}`} aria-label="Notificaciones">
        <Bell size={14} strokeWidth={1.7}/>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-[#b91c1c] text-white text-[9px] font-medium rounded-full flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`${typeof window !== 'undefined' && window.innerWidth < 640
            ? 'fixed left-1/2 -translate-x-1/2 w-[calc(100vw-1.5rem)] max-w-[380px]'
            : 'absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-1rem)]'} bg-white border border-[#e8e5de] rounded-xl shadow-2xl z-[1100] overflow-hidden fade-in`}
          style={typeof window !== 'undefined' && window.innerWidth < 640
            ? { top: 'calc(env(safe-area-inset-top, 0px) + 4.25rem)' }
            : undefined}
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-[#e8e5de] flex items-center justify-between">
            <div>
              <p className="text-[13px] font-medium tracking-tight">Notificaciones</p>
              {count > 0 && <p className="text-[11px] text-[#8a8579]">{count} sin leer</p>}
            </div>
            {count > 0 && (
              <button onClick={markAllRead} className="text-[11px] text-[#1c1c1c] inline-flex items-center gap-1 hover:underline">
                <CheckCheck size={11}/> Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <Inbox size={22} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
                <p className="text-[12px] text-[#8a8579]">Sin notificaciones</p>
              </div>
            ) : items.map(n => (
              <button key={n.id} onClick={() => onItemClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#f3f0e9] last:border-0 flex items-start gap-3 transition ${!n.read ? 'bg-[#fffbeb] hover:bg-[#fef3c7]' : 'hover:bg-[#f8f6f1]'}`}>
                <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-[12px] shrink-0 font-medium ${
                  n.type === 'price_approved' ? 'bg-[#1c1c1c] text-[#f8f6f1]' :
                  n.type === 'price_rejected' ? 'bg-[#fee2e2] text-[#991b1b]' :
                  n.type === 'price_alert' ? 'bg-[#fee2e2] text-[#991b1b]' :
                  n.type === 'weekly_summary' ? 'bg-[#e0e7ff] text-[#0f1d6f]' :
                  n.type === 'price_flagged' ? 'bg-[#fef3c7] text-[#92400e]' :
                  'bg-[#f3f0e9] text-[#3a3a3a]'
                }`}>
                  {typeIcon[n.type] || '·'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] leading-tight ${!n.read ? 'font-medium' : 'text-[#3a3a3a]'}`}>{n.title}</p>
                  {n.body && <p className="text-[12px] text-[#5a5a5a] mt-0.5 leading-snug truncate">{n.body}</p>}
                  <p className="text-[10.5px] text-[#8a8579] mt-1">hace {formatTime(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#b91c1c] mt-2 shrink-0"/>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
